import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button, Alert } from '../../components/primitives';
import { FormInput, FormCheckbox } from '../../components/form';
import { AuthLayout } from '../AuthLayout';

export default function Register() {
  const auth = useAuth();

  interface RegisterValues {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    passwordVerify: string;
    terms: boolean;
  }

  const [success, setSuccess] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  // Successful registration signs the user in (self-serve accounts are
  // created confirmed; the merchant approval queue is the vetting gate), so
  // the auth-state router swap takes over and lands on /complete-registration.
  // The success card is only a brief interstitial — no manual navigation.

  const registerValidation = Yup.object().shape({
    terms: Yup.boolean().required(),
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    email: Yup.string().email("Please enter a valid email address.").required("An email address is required"),
    password: Yup.string()
      .min(8)
      .max(16)
      .matches(
        /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
        'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and one special case Character'
      ),
    passwordVerify: Yup.string().oneOf(
      [Yup.ref('password'), null],
      'Passwords must match'
    ),
  });

  const handleOnSubmit = (
    values: RegisterValues,
    actions: FormikHelpers<RegisterValues>
  ) => {
    if (values.terms) {
      auth &&
        auth
          .register({
            password: values.password,
            email: values.email,
            given_name: values.firstName,
            family_name: values.lastName
          })
          .then(() => {
            actions.setSubmitting(false);
            setSuccess(true);
          })
          .catch((err) => {
            setSubmitError(err.message);
            actions.setSubmitting(false);
          });
    } else {
      setSubmitError(
        'You must agree to the terms and conditions before continuing.'
      );
      actions.setSubmitting(false);
    }
  };

  const handleOnChange = () => {
    setSubmitError(undefined);
  };

  if (success) {
    return <AuthLayout title="Registration Complete">Your registration is complete.</AuthLayout>;
  }

  return (
    <AuthLayout
      title="Register a new account"
      subtitle={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-primary hover:text-primary/80">
            Sign in
          </Link>
        </>
      }
      banner={
        submitError && (
          <Alert variant="destructive">
            {submitError}
          </Alert>
        )
      }
    >
      <Formik<RegisterValues>
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          passwordVerify: '',
          terms: false,
        }}
        onSubmit={handleOnSubmit}
        validationSchema={registerValidation}
      >
        {(props: FormikProps<RegisterValues>) => (
          <Form className="space-y-5" onChange={handleOnChange}>
            <FormInput
              name="firstName"
              id="firstName"
              label="First name"
              required
              disabled={props.isSubmitting}
            />
            <FormInput
              name="lastName"
              id="lastName"
              label="Last name"
              required
              disabled={props.isSubmitting}
            />
            <FormInput
              name="email"
              id="email"
              label="Email"
              required
              disabled={props.isSubmitting}
            />
            <FormInput
              name="password"
              id="password"
              label="Password"
              type="password"
              required
              disabled={props.isSubmitting}
            />
            <FormInput
              name="passwordVerify"
              id="passwordVerify"
              label="Re-enter password"
              type="password"
              required
              disabled={props.isSubmitting}
            />
            <FormCheckbox
              name="terms"
              id="terms"
              label="I accept the terms and conditions"
              disabled={props.isSubmitting}
            />
            <Button type="submit" className="w-full" disabled={props.isSubmitting}>
              {props.isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Register
            </Button>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
}
